import React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate } from 'react-router-dom';

import Logo from 'assets/images/Logo3.svg';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { clearUserData } from 'features/login/login';

interface NavigationItem {
    link: string;
    title: string;
}

const NAV_ITEMS: NavigationItem[] = [
    {
        link: '/tablets',
        title: 'Tablets',
    },
    {
        link: '/general/videos',
        title: 'General Videos',
    },
    {
        link: '/general/questionnaires',
        title: 'General Questionnaires',
    },
    {
        link: '/general/configurations',
        title: 'General Configurations',
    },
    {
        link: '/general/images',
        title: 'General Images',
    },
]

export const NavBar = () => {
    const [anchorElNav, setAnchorElNav] = React.useState(null);

    const user = useAppSelector(state => state.auth.user);

    const userIsLoggedIn = React.useMemo(() => {
        const token = localStorage.getItem('token');

        return token && user ? true : false;
    }, [user]);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const handleOpenNavMenu = React.useCallback((event: any) => {
        setAnchorElNav(event.currentTarget);
    }, []);

    const handleCloseNavMenu = React.useCallback((item: NavigationItem) => {
        navigate(item.link);
        setAnchorElNav(null);
    }, [navigate]);

    const handleLogout = React.useCallback(() => {
        localStorage.clear();
        navigate('/login');
        dispatch(clearUserData());
    }, [navigate]);

    const renderAppBar = React.useMemo(() => {
        if (!userIsLoggedIn) {
            return <div />;
        }

        return (
            <AppBar position="static" color="primary">
                <Container maxWidth="xl">
                    <Toolbar disableGutters={true}>
                        <Typography
                            variant="h6"
                            noWrap
                            component="div"
                            sx={{ mr: 2, display: { xs: 'none', md: 'flex' } }}
                        >
                            <img
                                style={{ height: '78px', width: 'auto'}}
                                src={Logo}
                                alt="logo"
                                loading="lazy"
                            />
                        </Typography>
                        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                            {NAV_ITEMS.map((item: NavigationItem) => (
                                <Button
                                    key={item.title}
                                    onClick={() => navigate(item.link)}
                                    sx={{ my: 2, color: 'white', display: 'block' }}
                                >
                                    {item.title}
                                </Button>
                            ))}
                        </Box>
                        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex', justifyContent: 'flex-end' } }}>
                            <Button color="inherit" onClick={handleLogout}>Logout</Button>
                        </Box>
                        <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                            <IconButton
                                size="large"
                                aria-label="account of current user"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleOpenNavMenu}
                                color="inherit"
                            >
                                <MenuIcon />
                            </IconButton>
                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorElNav}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                open={Boolean(anchorElNav)}
                                onClose={() => setAnchorElNav(null)}
                                sx={{
                                    display: { xs: 'block', md: 'none' },
                                }}
                            >
                                {NAV_ITEMS.map((item: NavigationItem) => (
                                    <MenuItem key={item.title} onClick={() => handleCloseNavMenu(item)}>
                                        <Typography textAlign="center">{item.title}</Typography>
                                    </MenuItem>
                                ))}
                                <MenuItem onClick={handleLogout}>
                                    <Typography textAlign="center">
                                        Logout
                                    </Typography>
                                </MenuItem>
                            </Menu>
                        </Box>
                        <Typography
                            variant="h6"
                            noWrap
                            component="div"
                            sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}
                        >
                            <img src={Logo} alt="logo" />
                        </Typography>
                    </Toolbar>
                </Container>
            </AppBar>
        );
    }, [userIsLoggedIn, anchorElNav]);

    return renderAppBar;
};
